import { CodeEditor, type CodeEditorRef } from '@/components/code/CodeEditor';
import { useColorLineMap } from '@/hooks/useColorLineMap';
import type { ExtractToFilePayload } from '@/components/code/ExtractToFileDialog';
import { buildExtractAllModulesPayloads } from '@/utils/scadExtractUtils';
import { ScadToolbar } from '@/components/code/ScadToolbar';
import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  type ImperativePanelHandle,
} from 'react-resizable-panels';
import { CollapsiblePanelContent } from '@/components/ui/collapsible-panel';
import { ViewerSection } from '@/components/viewer/ViewerSection';
import { ParameterSection } from '@/components/parameter/ParameterSection';
import { DownloadButton } from '@/components/parameter/DownloadButton';
import { EditorToolbar } from '@/components/editor/EditorToolbar';
import {
  SaveModelDialog,
  SaveModelData,
} from '@/components/model/SaveModelDialog';
import { ShareDialog } from '@/components/share/ShareDialog';
import { VersionHistory } from '@/components/version/VersionHistory';
import { FileManager, type ProjectFile } from '@/components/files/FileManager';
import { AiDialog } from '@/components/ai/AiDialog';
import { AiExplainDialog } from '@/components/ai/AiExplainDialog';
import { AiImproveDialog } from '@/components/ai/AiImproveDialog';
import { ScreenshotDialog } from '@/components/viewer/ScreenshotDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MeshpitLogo } from '@/components/MeshpitLogo';
import { Button } from '@/components/ui/button';
import { Parameter, ParameterType, InteractiveConfig } from '@shared/types';
import {
  updateParameter,
  generateDisplayName,
  parseParameters,
  parseSnapshots,
} from '@/utils/parameterUtils';
import { getMissingImports } from '@/utils/importUtils';
import { splitSvg } from '@/utils/svgSplitter';
import { randomizeParameters } from '@/utils/randomizeUtils';
import { downloadSTLFile } from '@/utils/downloadUtils';
import { BlobProvider } from '@/contexts/BlobContext';
import { ColorProvider } from '@/contexts/ColorContext';
import {
  SceneSettingsProvider,
  useSceneSettings,
} from '@/contexts/SceneSettingsContext';
import { useAuth } from '@/contexts/AuthContext';
import { CommandPalette } from '@/components/palette/CommandPalette';
import { useCommandRegistry } from '@/commands/useCommandRegistry';
import type { SlashAiHandlerOptions } from '@/commands/slashAiHandlers';
import { useRecentCommands } from '@/hooks/useRecentCommands';
import { useProductScore } from '@/hooks/useProductScore';
import {
  DEFAULT_VIEWER_STATE,
  type ViewerState,
} from '@/components/viewer/toolbar/ViewerToolbar';
import { useBlob } from '@/contexts/BlobContext';
import { McpTools } from '@/mcp/McpTools';
import {
  useCreateModel,
  useUpdateModel,
  useModelVersions,
  useRestoreVersion,
  useUploadThumbnail,
} from '@/hooks/useModels';
import { useUpsertModelFile, useDeleteModelFile } from '@/hooks/useModelFiles';
import { captureCurrentView } from '@/utils/thumbnailUtils';
import type { ModelWithAuthor, ModelVersion } from '@/lib/supabase/types';
import OpenSCADError from '@/lib/OpenSCADError';
import { verifySessionPurchase } from '@/services/purchases';
import { buildImproveParamsSystemPrompt } from '@/services/aiPrompts';
import { Wrench, CheckCircle2 } from 'lucide-react';

import { DEFAULT_SCAD_CODE } from '@/lib/scad-constants';
import type { ProductScore } from '@/hooks/useProductScore';

// ---------------------------------------------------------------------------
// Inner component: product readiness score
// Needs to live inside SceneSettingsProvider to access sceneLights/sceneOverrides.
// ---------------------------------------------------------------------------
function ScadViewerProductScoreSync({
  parameters,
  scadCode,
  cameraPath,
  thumbnailUrl,
  isPublic,
  title,
  onScoreChange,
}: {
  parameters: import('@shared/types').Parameter[];
  scadCode: string;
  cameraPath: import('@/worker/types').CameraPathWaypoint[];
  thumbnailUrl: string | null;
  isPublic: boolean;
  title: string;
  onScoreChange: (score: ProductScore) => void;
}) {
  const { sceneLights, sceneOverrides } = useSceneSettings();
  const score = useProductScore({
    parameters,
    scadCode,
    sceneLights,
    sceneOverrides,
    cameraPath,
    thumbnailUrl,
    isPublic,
    title,
  });
  // Sync score to parent whenever it changes
  useEffect(() => {
    onScoreChange(score);
  }, [score, onScoreChange]);
  return null;
}

// Inner component that has access to SceneSettingsContext (for SSAO/bloom/shadows)
function ScadViewerCommandPaletteInner({
  open,
  onOpenChange,
  editorRef,
  onSave,
  onSaveAs,
  onShare,
  onScreenshot,
  onShowHistory,
  onAiAssist,
  onToggleVisibility,
  isPublic,
  isOwner,
  hasModel,
  viewerState,
  onViewerStateChange,
  onFitToBounds,
  parameters,
  onParametersChange,
  onCreateSnapshot,
  onImportParameters,
  scadCode,
  onUploadFile,
  onCaptureMesh,
  onLayoutChange,
  onProductMode,
  onRecompile,
  stlBlobRef,
  recents,
  onRecordUsed,
  modelTitle,
  isDirty,
  lastSavedAt,
  slashAiHandlers,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editorRef: React.RefObject<CodeEditorRef | null>;
  onSave?: () => void;
  onSaveAs?: () => void;
  onShare?: () => void;
  onScreenshot?: () => void;
  onShowHistory?: () => void;
  onAiAssist?: () => void;
  onToggleVisibility?: () => void;
  isPublic?: boolean;
  isOwner?: boolean;
  hasModel?: boolean;
  viewerState: ViewerState;
  onViewerStateChange: (updater: (prev: ViewerState) => ViewerState) => void;
  onFitToBounds?: () => void;
  parameters: Parameter[];
  onParametersChange: (params: Parameter[]) => void;
  onCreateSnapshot?: () => void;
  onImportParameters?: () => void;
  scadCode?: string;
  onUploadFile?: () => void;
  onCaptureMesh?: () => void;
  onLayoutChange?: (
    layout: 'code-preview' | 'all' | 'preview-parameters',
  ) => void;
  onProductMode?: () => void;
  onRecompile?: () => void;
  /** Ref written every render so the outer keyboard handler can read stlBlob */
  stlBlobRef?: React.MutableRefObject<Blob | null>;
  recents: string[];
  onRecordUsed: (id: string) => void;
  modelTitle?: string;
  isDirty?: boolean;
  lastSavedAt?: string | null;
  /** Slash-command AI handlers */
  slashAiHandlers?: SlashAiHandlerOptions;
}) {
  const { settings, updateSSAO, updateBloom, updateShadow } =
    useSceneSettings();
  const { blob: stlBlob } = useBlob();

  // Keep stlBlobRef in sync so the outer keyboard handler (outside BlobProvider) can read it
  if (stlBlobRef) stlBlobRef.current = stlBlob ?? null;

  const commands = useCommandRegistry({
    editorRef,
    onSave,
    onSaveAs,
    onShare,
    onScreenshot,
    onShowHistory,
    onAiAssist,
    onToggleVisibility,
    isPublic,
    isOwner,
    hasModel,
    viewerState,
    onViewerStateChange,
    ssaoEnabled: settings.ssao.enabled,
    onSSAOToggle: (enabled) => updateSSAO({ enabled }),
    bloomEnabled: settings.bloom.enabled,
    onBloomToggle: (enabled) => updateBloom({ enabled }),
    softShadowsEnabled: settings.shadow.soft && settings.shadow.enabled,
    onSoftShadowsToggle: (enabled) =>
      updateShadow({ enabled: true, soft: enabled }),
    onFitToBounds,
    parameters,
    onParametersChange,
    onCreateSnapshot,
    onImportParameters,
    stlBlob: stlBlob ?? null,
    scadCode,
    onUploadFile,
    onCaptureMesh,
    onLayoutChange,
    onProductMode,
    onRecompile,
  });

  return (
    <>
      <CommandPalette
        open={open}
        onOpenChange={onOpenChange}
        commands={commands}
        recents={recents}
        onRecordUsed={onRecordUsed}
        slashAiHandlers={slashAiHandlers}
        onInsertCode={(text) => editorRef.current?.insertText(text)}
        scadCode={scadCode}
      />
      <McpTools
        commands={commands}
        scadCode={scadCode ?? ''}
        onSetCode={(code) => {
          if (editorRef.current) {
            editorRef.current.setValueWithUndo(code);
          }
        }}
        parameters={parameters}
        onParametersChange={onParametersChange ?? (() => {})}
        viewerState={viewerState}
        modelInfo={{
          title: modelTitle ?? 'Untitled Model',
          isPublic: isPublic ?? true,
          isDirty: isDirty ?? false,
          lastSavedAt: lastSavedAt ?? null,
          hasModel: hasModel ?? false,
        }}
      />
    </>
  );
}

export interface ScadViewerProps {
  /**
   * Replace the EditorToolbar with a custom element (e.g. the Examples
   * slideshow nav). The override is rendered in the same h-11 slot and
   * receives no automatic props — the caller is responsible for all controls.
   */
  toolbarOverride?: ReactNode;
  /**
   * Seed the editor with this SCAD code instead of the default template or
   * anything stored in localStorage. Useful when embedding the viewer in
   * controlled contexts such as the Examples page.
   */
  initialScadCode?: string;
}

export function ScadViewer({
  toolbarOverride,
  initialScadCode,
}: ScadViewerProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isAuthenticated, user } = useAuth();

  // Check if we're forking from another model
  const forkFrom = location.state?.forkFrom as ModelWithAuthor | undefined;

  const [scadCode, setScadCode] = useState(() => {
    if (initialScadCode) return initialScadCode;
    if (forkFrom?.scad_code) return forkFrom.scad_code;
    const stored = localStorage.getItem('scadblock_open_in_playground');
    if (stored) {
      localStorage.removeItem('scadblock_open_in_playground');
      return stored;
    }
    return DEFAULT_SCAD_CODE;
  });
  const [parameters, setParameters] = useState<Parameter[]>([]);

  // Derive code-defined snapshots from scadCode whenever it changes
  const codeSnapshots = useMemo(() => parseSnapshots(scadCode), [scadCode]);

  // Project files state
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [filesExpanded, setFilesExpanded] = useState(false);
  // editingFileId: null = editing main SCAD file; string = editing a project file
  const [editingFileId, setEditingFileId] = useState<string | null>(null);

  // Editor state
  const [title, setTitle] = useState(
    forkFrom ? `Fork of ${forkFrom.title}` : 'Untitled Model',
  );
  const [description, setDescription] = useState(forkFrom?.description || '');
  const [isPublic, setIsPublic] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [currentModel, setCurrentModel] = useState<ModelWithAuthor | null>(
    null,
  );
  // Download price in dollars (null = free), sourced from currentModel
  const [downloadPrice, setDownloadPrice] = useState<number | null>(null);
  // Post-payment success banner state
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const initialCodeRef = useRef(scadCode);
  const editorRef = useRef<CodeEditorRef>(null);

  // Panel refs for collapse/expand control
  const codePanelRef = useRef<ImperativePanelHandle>(null);
  const previewPanelRef = useRef<ImperativePanelHandle>(null);
  const parametersPanelRef = useRef<ImperativePanelHandle>(null);

  // Panel collapsed states
  const [isCodePanelCollapsed, setIsCodePanelCollapsed] = useState(false);
  const [isPreviewPanelCollapsed, setIsPreviewPanelCollapsed] = useState(false);
  const [isParametersPanelCollapsed, setIsParametersPanelCollapsed] =
    useState(false);

  // Mutations
  const createModel = useCreateModel();
  const updateModel = useUpdateModel();
  const uploadThumbnail = useUploadThumbnail();
  const restoreVersionMutation = useRestoreVersion();
  const upsertFile = useUpsertModelFile();
  const deleteFile = useDeleteModelFile();

  // Version history
  const { data: versions = [] } = useModelVersions(currentModel?.id || null);

  // Product mode state
  const [isProductMode, setIsProductMode] = useState(false);

  // Camera path — lifted from OpenSCADViewer via onCameraPathChange for the product score
  const [cameraPath, setCameraPath] = useState<import('@/worker/types').CameraPathWaypoint[]>([]);

  // Product readiness score — computed inside SceneSettingsProvider by ScadViewerProductScoreSync
  const [productScore, setProductScore] = useState<ProductScore | null>(null);
  const handleScoreChange = useCallback((score: ProductScore) => {
    setProductScore(score);
  }, []);

  // Viewer state — lifted here so the command palette can drive it
  const [viewerState, setViewerState] =
    useState<ViewerState>(DEFAULT_VIEWER_STATE);

  // Fit-to-bounds function exposed by ViewerSection
  const [fitToBoundsCmd, setFitToBoundsCmd] = useState<(() => void) | null>(
    null,
  );
  const handleFitToBoundsReady = useCallback((fitFn: () => void) => {
    setFitToBoundsCmd(() => fitFn);
  }, []);

  // ── Pub/sub bridge: viewer ↔ parameter panel ────────────────────────────
  // Stable refs to the publish and subscribe functions surfaced by ThreeScene
  // via ViewerSection. These are populated once on mount and never change.
  const pubSubPublishRef = useRef<((key: string, value: unknown) => void) | null>(null);
  const pubSubSubscribeRef = useRef<
    ((key: string, callback: (value: unknown) => void) => () => void) | null
  >(null);

  const handlePubSubPublish = useCallback(
    (fn: (key: string, value: unknown) => void) => {
      pubSubPublishRef.current = fn;
    },
    [],
  );
  const handleSubscribeReady = useCallback(
    (fn: (key: string, callback: (value: unknown) => void) => () => void) => {
      pubSubSubscribeRef.current = fn;
    },
    [],
  );

  // ── Colour indicator bar state ────────────────────────────────────────────
  /** Raw RGBA tuples from the most recent compile, received from ViewerSection. */
  const [compiledColors, setCompiledColors] = useState<
    [number, number, number, number][]
  >([]);
  /** Name of the parameter currently hovered in the sidebar. */
  const [hoveredParamName, setHoveredParamName] = useState<string | null>(null);

  /** Map: 1-based line number → RGBA for every color() call in the source. */
  const colorLineMap = useColorLineMap(scadCode, compiledColors, parameters);

  /** 1-based line number of the hovered parameter's declaration, or null. */
  const hoveredParamLine = useMemo(() => {
    if (!hoveredParamName) return null;
    const param = parameters.find((p) => p.name === hoveredParamName);
    return param?.lineNumber ?? null;
  }, [hoveredParamName, parameters]);

  // Action refs for command palette
  const parameterSectionActionRef = useRef<
    | import('@/components/parameter/ParameterSection').ParameterSectionActions
    | null
  >(null);
  const fileManagerActionRef = useRef<
    import('@/components/files/FileManager').FileManagerActions | null
  >(null);

  // Ref written by the inner (BlobProvider-aware) component so the outer
  // keyboard handler can access the current STL blob for ⌘D download
  const stlBlobRef = useRef<Blob | null>(null);

  // Command palette state
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const { recents, recordUsed } = useRecentCommands();

  // Dialog states
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiDialogMode, setAiDialogMode] = useState<'create' | 'modify'>('create');
  const [aiDialogInitialPrompt, setAiDialogInitialPrompt] = useState<string | undefined>(undefined);
  const [showScreenshotDialog, setShowScreenshotDialog] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showNewConfirmDialog, setShowNewConfirmDialog] = useState(false);

  // Incremented to force a recompile even when code hasn't changed (F5 / Ctrl+Enter)
  const [forceRecompileKey, setForceRecompileKey] = useState(0);
  const [showExplainDialog, setShowExplainDialog] = useState(false);
  const [explainSelection, setExplainSelection] = useState<{
    selectedCode: string;
    contextCode: string;
  } | null>(null);

  const [showImproveDialog, setShowImproveDialog] = useState(false);
  const [improveSelection, setImproveSelection] = useState<{
    selectedCode: string;
    contextCode: string;
    selectionRange: {
      startLineNumber: number;
      startColumn: number;
      endLineNumber: number;
      endColumn: number;
    };
  } | null>(null);

  const [showImproveParamsDialog, setShowImproveParamsDialog] = useState(false);
  const [improveParamsCode, setImproveParamsCode] = useState<string | null>(
    null,
  );

  const isSaving = createModel.isPending || updateModel.isPending;

  // Calculate missing imports
  const missingImports = getMissingImports(
    scadCode,
    projectFiles.map((f) => f.filename),
  );

  const isUiUpdateRef = useRef(false);

  // Sync download price from currentModel (converted from cents to dollars)
  useEffect(() => {
    if (currentModel?.download_price != null) {
      setDownloadPrice(currentModel.download_price / 100);
    } else {
      setDownloadPrice(null);
    }
  }, [currentModel]);

  // Detect Stripe return: ?stripe_session_id=xxx means the user just paid
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('stripe_session_id');
    if (!sessionId) return;

    // Clean up the URL immediately
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    // Verify the purchase is complete (webhook may have arrived by now)
    verifySessionPurchase(sessionId).then((confirmed) => {
      if (confirmed) {
        setPaymentSuccess(true);
        // Hide the banner after 6 seconds
        setTimeout(() => setPaymentSuccess(false), 6000);
      }
    });
  }, []);

  // Parse parameters when code changes
  useEffect(() => {
    const newParams = parseParameters(scadCode);
    setParameters(newParams);
  }, []);

  // Track dirty state
  useEffect(() => {
    if (initialCodeRef.current !== scadCode) {
      setIsDirty(true);
    }
  }, [scadCode]);

  const handleError = useCallback((error: OpenSCADError | Error | null) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editor = editorRef.current?.editor as any;
    const monaco = editorRef.current?.monaco;

    if (!editor || !monaco) return;

    if (!error) {
      monaco.editor.setModelMarkers(editor.getModel(), 'owner', []);
      return;
    }

    // The error arrives as a plain serialized object from the worker, so check
    // for the name property rather than using instanceof.
    const stdErr: string[] | undefined =
      'stdErr' in error && Array.isArray((error as OpenSCADError).stdErr)
        ? (error as OpenSCADError).stdErr
        : undefined;

    if (stdErr) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const markers: any[] = [];
      const regex = /line (\d+)(?::\s*(.*))?/;

      stdErr.forEach((line) => {
        // Only look at lines mentioning input.scad or just line numbers if filename is implicit
        if (line.includes('"input.scad"') || !line.includes('file "')) {
          const match = line.match(regex);
          if (match) {
            const lineNumber = parseInt(match[1], 10);
            const message = match[2] || line;

            // Check if line number is valid
            const model = editor.getModel();
            if (lineNumber > model.getLineCount()) return;

            markers.push({
              startLineNumber: lineNumber,
              startColumn: 1,
              endLineNumber: lineNumber,
              endColumn: model.getLineMaxColumn(lineNumber),
              message: message,
              severity: line.includes('WARNING')
                ? monaco.MarkerSeverity.Warning
                : monaco.MarkerSeverity.Error,
            });
          }
        }
      });

      monaco.editor.setModelMarkers(editor.getModel(), 'owner', markers);
    } else {
      monaco.editor.setModelMarkers(editor.getModel(), 'owner', []);
    }
  }, []);

  const handleJumpToLine = useCallback((lineNumber: number) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editor = editorRef.current?.editor as any;
    if (!editor) return;
    editor.revealLineInCenter(lineNumber);
    editor.setPosition({ lineNumber, column: 1 });
    editor.focus();
  }, []);

  // Returns the Monaco language ID for a given filename
  const getLanguageForFile = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'scad':
        return 'openscad';
      case 'js':
        return 'javascript';
      case 'json':
        return 'json';
      case 'svg':
        return 'xml';
      default:
        return 'plaintext';
    }
  };

  // Derive current editor props based on whether we're editing a project file
  const editingFile = editingFileId
    ? (projectFiles.find((f) => f.id === editingFileId) ?? null)
    : null;
  const editorValue = editingFile ? (editingFile.content as string) : scadCode;
  const editorLanguage = editingFile
    ? getLanguageForFile(editingFile.filename)
    : 'openscad';
  const editorFilePath = editingFile ? editingFile.filename : 'input.scad';
  // Show ScadToolbar when on main file or when editing a .scad project file
  const showScadToolbar =
    editingFile === null || editingFile.fileType === 'scad';

  const handleCodeChange = (value: string | undefined) => {
    if (value === undefined) return;
    if (editingFileId) {
      // Update the project file's content in place
      setProjectFiles((prev) =>
        prev.map((f) =>
          f.id === editingFileId
            ? {
                ...f,
                content: value,
                size: new Blob([value]).size,
                isModified: true,
              }
            : f,
        ),
      );
      setIsDirty(true);
    } else {
      setScadCode(value);
      if (!isUiUpdateRef.current) {
        setParameters(parseParameters(value));
      }
      setIsDirty(true);
    }
  };

  // File management handlers
  const handleFileAdd = useCallback((file: ProjectFile) => {
    setProjectFiles((prev) => {
      // Check if file with same name exists
      const existing = prev.find((f) => f.filename === file.filename);
      if (existing) {
        // Replace existing file
        return prev.map((f) => (f.filename === file.filename ? file : f));
      }
      return [...prev, file];
    });
    setIsDirty(true);
  }, []);

  const handleFileDelete = useCallback((fileId: string) => {
    setProjectFiles((prev) => prev.filter((f) => f.id !== fileId));
    setActiveFileId((prev) => (prev === fileId ? null : prev));
    setEditingFileId((prev) => (prev === fileId ? null : prev));
    setIsDirty(true);
  }, []);

  const handleFileSelect = useCallback(
    (fileId: string) => {
      const file = projectFiles.find((f) => f.id === fileId);
      if (!file) return;
      setActiveFileId(fileId);
      // Open non-binary files directly in the editor
      if (!file.isBinary && typeof file.content === 'string') {
        setEditingFileId(fileId);
      }
    },
    [projectFiles],
  );

  const handleReturnToMain = useCallback(() => {
    setEditingFileId(null);
    setActiveFileId(null);
  }, []);

  const handleFileRename = useCallback(
    (fileId: string, newFilename: string) => {
      setProjectFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, filename: newFilename } : f,
        ),
      );
      setIsDirty(true);
    },
    [],
  );

  const handleInsertImport = useCallback(
    (importStatement: string) => {
      // Insert at the beginning of the code (after any existing use/include statements)
      const lines = scadCode.split('\n');
      let insertIndex = 0;

      // Find the last use/include statement
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith('use ') || line.startsWith('include ')) {
          insertIndex = i + 1;
        } else if (line && !line.startsWith('//') && !line.startsWith('/*')) {
          // Stop at first non-comment, non-empty line that isn't use/include
          break;
        }
      }

      // Insert the import statement
      lines.splice(insertIndex, 0, importStatement);
      const newCode = lines.join('\n');

      setScadCode(newCode);
      setParameters(parseParameters(newCode));
      setIsDirty(true);
    },
    [scadCode],
  );

  const handleCaptureMesh = useCallback(
    (capturedFiles: ProjectFile[]) => {
      for (const file of capturedFiles) {
        handleFileAdd(file);
      }
    },
    [handleFileAdd],
  );

  const handleSvgSplit = useCallback(
    async (fileId: string): Promise<string[]> => {
      const file = projectFiles.find((f) => f.id === fileId);
      if (!file || file.fileType !== 'svg') {
        throw new Error('File not found or is not an SVG');
      }
      if (typeof file.content !== 'string') {
        throw new Error('SVG content is not readable');
      }

      const result = splitSvg(file.content, file.filename);

      for (const splitFile of result.files) {
        const newProjectFile: ProjectFile = {
          id: crypto.randomUUID(),
          filename: splitFile.filename,
          fileType: 'svg',
          content: splitFile.content,
          size: splitFile.size,
          isBinary: false,
          isModified: true,
        };
        handleFileAdd(newProjectFile);
      }

      return result.files.map((f) => f.filename);
    },
    [projectFiles, handleFileAdd],
  );

  const handleExtractToFile = useCallback(
    (payload: ExtractToFilePayload) => {
      const {
        filename,
        fileContent,
        callCode,
        importStatement,
        selectionRange,
      } = payload;

      // 1. Create the new project file
      const newFile = {
        id: crypto.randomUUID(),
        filename,
        fileType: 'scad' as const,
        content: fileContent,
        size: new Blob([fileContent]).size,
        isBinary: false,
      };
      setProjectFiles((prev) => {
        // Replace if a file with the same name already exists
        const existing = prev.find((f) => f.filename === filename);
        if (existing) {
          return prev.map((f) => (f.filename === filename ? newFile : f));
        }
        return [...prev, newFile];
      });

      // 2. Compute the updated main code:
      //    a. Replace the selected range with the module call
      //    b. Insert the include statement at the top (after existing use/include lines)
      const lines = scadCode.split('\n');

      // Replace the selected lines with the call code
      const startLine = selectionRange.startLineNumber - 1; // 0-indexed
      const endLine = selectionRange.endLineNumber - 1;
      const startCol = selectionRange.startColumn - 1;
      const endCol = selectionRange.endColumn - 1;

      // Build the patched code with the selection replaced by the call
      const before =
        lines.slice(0, startLine).join('\n') +
        (startLine > 0 ? '\n' : '') +
        lines[startLine].slice(0, startCol);
      const after =
        lines[endLine].slice(endCol) +
        (endLine < lines.length - 1 ? '\n' : '') +
        lines.slice(endLine + 1).join('\n');

      const replacedCode = before + callCode + after;

      // Insert the include statement at the top (after existing use/include lines)
      const replacedLines = replacedCode.split('\n');
      let includeLineOffset = 0; // how many lines were prepended before startLine
      if (!replacedLines.some((l) => l.trim() === importStatement)) {
        let insertIdx = 0;
        for (let i = 0; i < replacedLines.length; i++) {
          const l = replacedLines[i].trim();
          if (l.startsWith('use ') || l.startsWith('include ')) {
            insertIdx = i + 1;
          } else if (l && !l.startsWith('//') && !l.startsWith('/*')) {
            break;
          }
        }
        replacedLines.splice(insertIdx, 0, importStatement);
        // If the include was inserted at or before the call's line, the call shifts down
        if (insertIdx <= startLine) {
          includeLineOffset = 1;
        }
      }

      const newCode = replacedLines.join('\n');

      // 3. Update editor and state
      if (editorRef.current) {
        editorRef.current.setValueWithUndo(newCode);
        // Deselect: place cursor at the start of where the call code landed
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const editor = editorRef.current.editor as any;
        if (editor) {
          const callLine = startLine + 1 + includeLineOffset; // 1-indexed
          editor.setSelection({
            startLineNumber: callLine,
            startColumn: 1,
            endLineNumber: callLine,
            endColumn: 1,
          });
          editor.revealLineInCenterIfOutsideViewport(callLine);
        }
      } else {
        setScadCode(newCode);
      }
      setParameters(parseParameters(newCode));
      setIsDirty(true);

      // 4. Expand the file manager so the user sees the new file
      setFilesExpanded(true);
    },
    [scadCode],
  );

  const handleExtractModulesToFiles = useCallback(() => {
    const payloads = buildExtractAllModulesPayloads(scadCode);
    if (payloads.length === 0) return;

    // 1. Create all new project files
    const newFiles = payloads.map((p) => ({
      id: crypto.randomUUID(),
      filename: p.filename,
      fileType: 'scad' as const,
      content: p.fileContent,
      size: new Blob([p.fileContent]).size,
      isBinary: false,
    }));

    setProjectFiles((prev) => {
      let next = [...prev];
      for (const f of newFiles) {
        const existing = next.find((x) => x.filename === f.filename);
        if (existing) {
          next = next.map((x) => (x.filename === f.filename ? f : x));
        } else {
          next = [...next, f];
        }
      }
      return next;
    });

    // 2. Rewrite the main code:
    //    - Process modules in reverse order so that earlier line numbers remain
    //      valid as we splice lines out.
    //    - Replace each module definition with its call code.
    //    - Collect all include statements to prepend at the top.
    let lines = scadCode.split('\n');

    // Sort payloads bottom-to-top
    const sorted = [...payloads].sort((a, b) => b.startLine - a.startLine);

    for (const p of sorted) {
      const start = p.startLine - 1; // 0-indexed
      const end = p.endLine - 1; // 0-indexed, inclusive
      // callCode is '' for module definitions — remove the lines entirely
      // rather than leaving a blank line.
      if (p.callCode === '') {
        lines.splice(start, end - start + 1);
      } else {
        lines.splice(start, end - start + 1, p.callCode);
      }
    }

    // 3. Insert include statements at the top (after any existing use/include lines),
    //    deduplicating against lines already present.
    const includeStatements = payloads.map((p) => p.importStatement);
    let insertIdx = 0;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i].trim();
      if (l.startsWith('use ') || l.startsWith('include ')) {
        insertIdx = i + 1;
      } else if (l && !l.startsWith('//') && !l.startsWith('/*')) {
        break;
      }
    }
    const toInsert = includeStatements.filter(
      (stmt) => !lines.some((l) => l.trim() === stmt),
    );
    lines.splice(insertIdx, 0, ...toInsert);

    const newCode = lines.join('\n');

    // 4. Update editor and state
    if (editorRef.current) {
      editorRef.current.setValueWithUndo(newCode);
    } else {
      setScadCode(newCode);
    }
    setParameters(parseParameters(newCode));
    setIsDirty(true);
    setFilesExpanded(true);
  }, [scadCode]);

  const handleParametersChange = useCallback(
    (newParams: Parameter[]) => {
      let newCode = scadCode;
      newParams.forEach((param) => {
        newCode = updateParameter(newCode, param);
      });

      if (editorRef.current) {
        isUiUpdateRef.current = true;
        editorRef.current.setValueWithUndo(newCode);
        isUiUpdateRef.current = false;

        // Ensure parameters state gets updated correctly for the UI
        setParameters(newParams);
        setIsDirty(true);
      } else {
        setScadCode(newCode);
        setParameters(newParams);
        setIsDirty(true);
      }
    },
    [scadCode],
  );

  /**
   * Handle a click on an interactive() region in the 3D viewer.
   *
   * Behaviour:
   * - If the interactive call specified `options` (array form), cycle the
   *   variable to the next value in the list, wrapping around after the last.
   * - If the interactive call specified an explicit `value`, set the variable
   *   to that value (enables radio-button / selector patterns).
   * - Otherwise, toggle the variable as a boolean (false → true → false).
   *
   * The update goes through the same handleParametersChange() path as a
   * parameter panel change — updateParameter() rewrites the SCAD source,
   * the Monaco editor reflects the change with undo support, and the normal
   * compile debounce fires.
   */
  const handleInteraction = useCallback(
    (config: InteractiveConfig) => {
      const param = parameters.find((p) => p.name === config.variable);
      if (!param) return;

      let newValue: Parameter['value'];
      if (config.options !== undefined && config.options.length > 0) {
        // Cycle mode: advance to the next option in the list, wrapping around.
        const current = param.value;
        const opts = config.options as Array<string | number>;
        const currentIdx = opts.findIndex((o) => String(o) === String(current));
        const nextIdx = currentIdx === -1 ? 0 : (currentIdx + 1) % opts.length;
        newValue = opts[nextIdx];
      } else if (config.value !== undefined) {
        // Explicit-value mode: set the variable to the specified value
        newValue = config.value;
      } else {
        // Toggle mode: flip boolean (treat any truthy string/value as true)
        const current = param.value;
        newValue = current === true || current === 'true' ? false : true;
      }

      const updatedParam: Parameter = { ...param, value: newValue };
      handleParametersChange(
        parameters.map((p) => (p.name === config.variable ? updatedParam : p)),
      );
    },
    [parameters, handleParametersChange],
  );

  const handleSave = () => {
    if (!isAuthenticated) {
      // TODO: Show login prompt
      return;
    }

    if (!currentModel) {
      // First save - show dialog
      setShowSaveDialog(true);
    } else {
      // Update existing model
      performUpdate();
    }
  };

  const handleSaveAs = () => {
    setShowSaveDialog(true);
  };

  const performSave = async (data: SaveModelData) => {
    if (!user) return;

    // Convert project files to the format expected by the service
    const filesToSave = projectFiles.map((f) => ({
      filename: f.filename,
      content: f.content,
      fileType: f.fileType,
    }));

    try {
      // Create new model
      const model = await createModel.mutateAsync({
        userId: user.id,
        input: {
          title: data.title,
          description: data.description || undefined,
          scadCode,
          isPublic: data.isPublic,
          forkedFrom: forkFrom?.id,
          files: filesToSave,
          downloadPrice: data.downloadPrice ?? null,
        },
      });

      // Capture and upload thumbnail
      setTimeout(async () => {
        const thumbnailBlob = await captureCurrentView();
        if (thumbnailBlob && model) {
          uploadThumbnail.mutate({
            modelId: model.id,
            thumbnailBlob,
          });
        }
      }, 1000); // Wait for render to complete

      // Update local state
      setCurrentModel(model);
      setTitle(model.title);
      setDescription(model.description || '');
      setIsPublic(model.is_public);
      setDownloadPrice(
        model.download_price != null ? model.download_price / 100 : null,
      );
      setIsDirty(false);
      setLastSavedAt(model.created_at);
      setThumbnailUrl(model.thumbnail_url);
      initialCodeRef.current = scadCode;

      // Navigate to the model's URL
      if (profile) {
        navigate(`/${profile.username}/${model.slug}`, { replace: true });
      }
    } catch (error) {
      console.error('Error saving model:', error);
      throw error;
    }
  };

  const performUpdate = async (commitMessage?: string) => {
    if (!currentModel) return;

    // Convert project files to the format expected by the service
    const filesToSave = projectFiles.map((f) => ({
      filename: f.filename,
      content: f.content,
      fileType: f.fileType,
    }));

    try {
      const updated = await updateModel.mutateAsync({
        modelId: currentModel.id,
        input: {
          title,
          description,
          scadCode,
          isPublic,
          files: filesToSave,
          commitMessage,
          downloadPrice,
        },
      });

      // Capture and upload thumbnail
      setTimeout(async () => {
        const thumbnailBlob = await captureCurrentView();
        if (thumbnailBlob) {
          uploadThumbnail.mutate({
            modelId: currentModel.id,
            thumbnailBlob,
          });
        }
      }, 1000);

      setIsDirty(false);
      setLastSavedAt(updated.updated_at);
      initialCodeRef.current = scadCode;
    } catch (error) {
      console.error('Error updating model:', error);
    }
  };

  const handleFork = () => {
    // Open save dialog as "Save as new"
    setShowSaveDialog(true);
  };

  const handleNewModel = () => {
    if (currentModel) {
      navigate('/new');
    } else {
      setShowNewConfirmDialog(true);
    }
  };

  const confirmNewModel = () => {
    setScadCode('');
    setParameters([]);
    setTitle('Untitled Model');
    setIsDirty(false);
    setProjectFiles([]);
    setEditingFileId(null);
    setShowNewConfirmDialog(false);
    initialCodeRef.current = '';
    editorRef.current?.setValueWithUndo('');
  };

  const handleShare = () => {
    if (currentModel && profile?.username) {
      setShowShareDialog(true);
    }
  };

  const handleShowHistory = () => {
    if (currentModel) {
      setShowVersionHistory(true);
    }
  };

  const handleAiAssist = () => {
    setAiDialogMode('create');
    setAiDialogInitialPrompt(undefined);
    setShowAiDialog(true);
  };

  const handleAiCreate = useCallback((prompt: string) => {
    setAiDialogMode('create');
    setAiDialogInitialPrompt(prompt || undefined);
    setShowAiDialog(true);
  }, []);

  const handleAiModify = useCallback((prompt: string) => {
    setAiDialogMode('modify');
    setAiDialogInitialPrompt(prompt || undefined);
    setShowAiDialog(true);
  }, []);

  const handleAiExplainSlash = useCallback(() => {
    const code = scadCode ?? '';
    setExplainSelection({ selectedCode: code, contextCode: code });
    setShowExplainDialog(true);
  }, [scadCode]);

  const handleAiImproveSlash = useCallback(() => {
    const code = scadCode ?? '';
    setImproveSelection({
      selectedCode: code,
      contextCode: code,
      selectionRange: { startLineNumber: 1, startColumn: 1, endLineNumber: 99999, endColumn: 1 },
    });
    setShowImproveDialog(true);
  }, [scadCode]);

  const handleExplainWithAi = useCallback(
    (selectedCode: string, contextCode: string) => {
      setExplainSelection({ selectedCode, contextCode });
      setShowExplainDialog(true);
    },
    [],
  );

  const handleImproveWithAi = useCallback(
    (
      selectedCode: string,
      contextCode: string,
      selectionRange: {
        startLineNumber: number;
        startColumn: number;
        endLineNumber: number;
        endColumn: number;
      },
    ) => {
      setImproveSelection({ selectedCode, contextCode, selectionRange });
      setShowImproveDialog(true);
    },
    [],
  );

  const handleApplyImprovedCode = useCallback(
    (improvedCode: string) => {
      if (!improveSelection) return;
      const editor = editorRef.current?.editor as any;
      if (!editor) return;
      const { startLineNumber, startColumn, endLineNumber, endColumn } =
        improveSelection.selectionRange;
      editor.pushUndoStop();
      editor.executeEdits('improve-with-ai', [
        {
          range: { startLineNumber, startColumn, endLineNumber, endColumn },
          text: improvedCode,
        },
      ]);
      editor.pushUndoStop();
    },
    [improveSelection],
  );

  const handleImproveParamsWithAi = useCallback((contextCode: string) => {
    setImproveParamsCode(contextCode);
    setShowImproveParamsDialog(true);
  }, []);

  const handleApplyImprovedParams = useCallback((improvedCode: string) => {
    editorRef.current?.setValueWithUndo(improvedCode);
  }, []);

  const handleScreenshot = () => {
    setShowScreenshotDialog(true);
  };

  const handleSetThumbnail = useCallback(
    (blob: Blob) => {
      if (!currentModel) return;
      uploadThumbnail.mutate(
        { modelId: currentModel.id, thumbnailBlob: blob },
        {
          onSuccess: (url) => {
            if (url) setThumbnailUrl(url);
          },
        },
      );
    },
    [currentModel, uploadThumbnail],
  );

  const handleAiCodeGenerated = useCallback((code: string) => {
    setScadCode(code);
    setParameters(parseParameters(code));
    setIsDirty(true);
  }, []);

  /** Appends code generated by viewer context-menu actions (e.g. PiP camera) to the end of the editor. */
  const handleInsertCode = useCallback((code: string) => {
    editorRef.current?.insertAtEnd('\n' + code);
  }, []);

  /** Replaces matched text in the editor (used by PiP drag-drop to update x/y coordinates). */
  const handleReplaceCode = useCallback(
    (search: string | RegExp, replacement: string) => {
      editorRef.current?.replaceText(search, replacement);
    },
    [],
  );

  const handlePreviewVersion = (version: ModelVersion) => {
    setScadCode(version.scad_code);
    setParameters(parseParameters(version.scad_code));
  };

  const handleRestoreVersion = async (version: ModelVersion) => {
    if (!currentModel) return;
    await restoreVersionMutation.mutateAsync({
      modelId: currentModel.id,
      version,
    });
    // Reload the restored code
    setScadCode(version.scad_code);
    setParameters(parseParameters(version.scad_code));
  };

  const handleLayoutChange = (
    newLayout: 'code-preview' | 'all' | 'preview-parameters',
  ) => {
    switch (newLayout) {
      case 'code-preview':
        codePanelRef.current?.expand();
        previewPanelRef.current?.expand();
        parametersPanelRef.current?.collapse();
        break;
      case 'all':
        codePanelRef.current?.expand();
        previewPanelRef.current?.expand();
        parametersPanelRef.current?.expand();
        break;
      case 'preview-parameters':
        codePanelRef.current?.collapse();
        previewPanelRef.current?.expand();
        parametersPanelRef.current?.expand();
        break;
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    /**
     * Returns true when a text-input element (Monaco editor, <input>,
     * <textarea>, or any contentEditable) currently has keyboard focus.
     * Single-letter shortcuts are skipped in this case so they don't type
     * characters into the editor.
     */
    const isInputFocused = (): boolean => {
      const el = document.activeElement;
      if (!el) return false;
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLElement && el.isContentEditable)
      ) {
        return true;
      }
      // Monaco editor mounts several div/textarea elements; detect by ancestor
      if (el.closest('.monaco-editor')) return true;
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // ── Modifier shortcuts (always active) ──────────────────────────────────
      // Using capture phase so these fire before Monaco's internal handlers.
      // stopPropagation prevents the event from ever reaching Monaco's textarea.
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        e.stopPropagation();
        handleSave();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setShowCommandPalette((prev) => !prev);
        return;
      }
      // Ctrl/Cmd+Enter → force recompile
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        setForceRecompileKey((k) => k + 1);
        return;
      }
      // Ctrl/Cmd+Shift+S → screenshot
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        e.stopPropagation();
        handleScreenshot();
        return;
      }
      // Ctrl/Cmd+Shift+A → AI assistant
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        e.stopPropagation();
        handleAiAssist();
        return;
      }
      // Ctrl/Cmd+D → download STL
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        e.stopPropagation();
        const blob = stlBlobRef.current;
        if (blob) downloadSTLFile(blob, undefined, [], parameters);
        return;
      }
      // Ctrl/Cmd+1/2/3 → layout switching
      if ((e.metaKey || e.ctrlKey) && e.key === '1') {
        e.preventDefault();
        e.stopPropagation();
        handleLayoutChange('code-preview');
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '2') {
        e.preventDefault();
        e.stopPropagation();
        handleLayoutChange('all');
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '3') {
        e.preventDefault();
        e.stopPropagation();
        handleLayoutChange('preview-parameters');
        return;
      }

      // ── Function-key shortcuts (always active) ──────────────────────────────
      if (e.key === 'F5') {
        e.preventDefault();
        e.stopPropagation();
        setForceRecompileKey((k) => k + 1);
        return;
      }
      if (e.key === 'F1') {
        e.preventDefault();
        e.stopPropagation();
        setShowHelp(true);
        return;
      }

      // ── File navigation: Shift+Alt+Left / Shift+Alt+Right ───────────────────
      if (e.shiftKey && e.altKey && !e.metaKey && !e.ctrlKey &&
          (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        e.stopPropagation();
        // Build ordered file list: null = main file, then project files
        const fileList: (string | null)[] = [null, ...projectFiles.map((f) => f.id)];
        const currentIndex = fileList.indexOf(editingFileId ?? null);
        const delta = e.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (currentIndex + delta + fileList.length) % fileList.length;
        const nextId = fileList[nextIndex];
        if (nextId === null) {
          handleReturnToMain();
        } else {
          handleFileSelect(nextId);
        }
        return;
      }

      // ── Single-letter shortcuts (blocked when an input has focus) ────────────
      if (isInputFocused()) return;

      // Ignore if any modifier key is held (except Shift for Shift+key combos)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case 'f':
        case 'F':
          e.preventDefault();
          fitToBoundsCmd?.();
          break;
        case 'g':
          e.preventDefault();
          setViewerState((prev) => ({ ...prev, showGrid: !prev.showGrid }));
          break;
        case 'G': // Shift+G → grayscale
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            grayscaleMode: !prev.grayscaleMode,
          }));
          break;
        case 'w':
        case 'W':
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            wireframeOpacity: prev.wireframeOpacity > 0 ? 0 : 1,
          }));
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            polygonMode: !prev.polygonMode,
          }));
          break;
        case 'o':
        case 'O':
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            isOrthographic: !prev.isOrthographic,
          }));
          break;
        case 'r': // lowercase r (no shift) → presentation mode
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            presentationMode: !prev.presentationMode,
          }));
          break;
        case 'R': // Shift+R → randomize parameters
          e.preventDefault();
          if (parameters.length > 0) {
            handleParametersChange(randomizeParameters(parameters));
          }
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          setViewerState((prev) => ({ ...prev, showAxis: !prev.showAxis }));
          break;
        case 'M': // Shift+M → measurement
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            measurementEnabled: !prev.measurementEnabled,
          }));
          break;
        case 'D': // Shift+D → reset parameters to default
          e.preventDefault();
          if (parameters.length > 0) {
            handleParametersChange(
              parameters.map((p) => ({ ...p, value: p.defaultValue })),
            );
          }
          break;
        case 'i':
        case 'I':
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            inspectorEnabled: !prev.inspectorEnabled,
          }));
          break;
        case 'e':
        case 'E':
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            showExcluded: !prev.showExcluded,
          }));
          break;
        case 'n':
        case 'N':
          e.preventDefault();
          setViewerState((prev) => ({
            ...prev,
            annotationsEnabled: !prev.annotationsEnabled,
          }));
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [
    currentModel,
    isAuthenticated,
    fitToBoundsCmd,
    parameters,
    handleParametersChange,
    projectFiles,
    editingFileId,
    handleFileSelect,
    handleReturnToMain,
  ]);

  // Handle layout changes for product mode
  useEffect(() => {
    if (isProductMode) {
      codePanelRef.current?.collapse();
      parametersPanelRef.current?.collapse();
    } else {
      codePanelRef.current?.expand();
      parametersPanelRef.current?.expand();
    }
  }, [isProductMode]);

  // Derived layout state
  const currentLayout =
    !isCodePanelCollapsed &&
    !isPreviewPanelCollapsed &&
    !isParametersPanelCollapsed
      ? 'all'
      : !isCodePanelCollapsed &&
          !isPreviewPanelCollapsed &&
          isParametersPanelCollapsed
        ? 'code-preview'
        : isCodePanelCollapsed &&
            !isPreviewPanelCollapsed &&
            !isParametersPanelCollapsed
          ? 'preview-parameters'
          : undefined;

  // Unified layout
  const layout = (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-background">
      {/* Product Mode Overlays */}
      {isProductMode && (
        <>
          {/* Logo overlay — top left */}
          <div className="pointer-events-none absolute left-4 top-4 z-10 flex items-center gap-2">
            <MeshpitLogo className="pointer-events-auto h-8 w-8 text-foreground drop-shadow-md" />
            <span className="pointer-events-auto text-lg font-semibold text-foreground drop-shadow-md">
              Meshpit
            </span>
          </div>

          {/* Post-payment success banner */}
          {paymentSuccess && (
            <div className="absolute left-1/2 top-4 z-20 -translate-x-1/2">
              <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-950/90 px-4 py-2.5 text-sm text-emerald-300 shadow-lg backdrop-blur-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                Payment confirmed — your download is ready!
              </div>
            </div>
          )}

          {/* Switch to Dev Mode button */}
          <div className="absolute left-40 top-4 z-10">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsProductMode(false)}
              className="gap-1.5 bg-background/80 backdrop-blur-sm"
            >
              <Wrench className="h-3.5 w-3.5" />
              Dev Mode
            </Button>
          </div>

          {/* Parameters overlay — right side */}
          {parameters.length > 0 && (
            <div className="absolute bottom-4 right-4 top-4 z-10 flex w-80 flex-col overflow-hidden rounded-lg border border-border/50 bg-background/80 shadow-lg backdrop-blur-md">
              <ParameterSection
                parameters={parameters}
                onParametersChange={handleParametersChange}
                scadCode={scadCode}
                projectFiles={projectFiles}
                onFileAdd={handleFileAdd}
                productMode
                pubSubPublishRef={pubSubPublishRef}
                pubSubSubscribeRef={pubSubSubscribeRef}
              />
            </div>
          )}

          {/* Download button overlay — bottom left */}
          <div className="absolute bottom-4 left-4 z-10">
            <DownloadButton
              scadCode={scadCode}
              price={downloadPrice}
              modelId={currentModel?.id ?? null}
              userId={user?.id ?? null}
              className="w-48 drop-shadow-md"
              parameters={parameters}
              projectFiles={projectFiles.map((f) => ({
                filename: f.filename,
                content: f.content,
                isBinary: f.isBinary,
              }))}
            />
          </div>
        </>
      )}

      {/* Editor Toolbar (Hidden in Product Mode) */}
      {!isProductMode && (
        toolbarOverride ?? (
          <EditorToolbar
            title={title}
            onTitleChange={setTitle}
            isPublic={isPublic}
            onVisibilityChange={setIsPublic}
            onSave={handleSave}
            onSaveAs={handleSaveAs}
            onFork={handleFork}
            onShare={handleShare}
            onShowHistory={currentModel ? handleShowHistory : undefined}
            onAiAssist={handleAiAssist}
            onScreenshot={handleScreenshot}
            thumbnailUrl={thumbnailUrl}
            isSaving={isSaving}
            isDirty={isDirty}
            lastSavedAt={lastSavedAt}
            isOwner={true}
            modelSlug={currentModel?.slug || null}
            onProductMode={() => setIsProductMode(true)}
            onNewModel={handleNewModel}
            layout={currentLayout}
            onLayoutChange={handleLayoutChange}
            helpOpen={showHelp}
            onHelpOpenChange={setShowHelp}
            productScore={productScore ?? undefined}
          />
        )
      )}

      {/* Main Editor Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Horizontal panels */}
        <div className="flex-1 overflow-hidden">
          <PanelGroup direction="horizontal">
            {/* Code Editor Panel */}
            <Panel
              ref={codePanelRef}
              defaultSize={33}
              minSize={4}
              collapsible={true}
              collapsedSize={isProductMode ? 0 : 4}
              onCollapse={() => setIsCodePanelCollapsed(true)}
              onExpand={() => setIsCodePanelCollapsed(false)}
              order={1}
            >
              <CollapsiblePanelContent
                isCollapsed={isCodePanelCollapsed}
                label="Code"
                onExpand={() => codePanelRef.current?.expand()}
                position="left"
                showStripe={!isProductMode}
              >
                <div className="flex h-full flex-col border-r border-border">
                  <ScadToolbar
                    editorRef={editorRef}
                    onCollapse={() => codePanelRef.current?.collapse()}
                    isMainFile={showScadToolbar}
                  />
                  <div className="flex-1 overflow-hidden">
                    <CodeEditor
                      ref={editorRef}
                      value={editorValue}
                      onChange={handleCodeChange}
                      onExtractToFile={
                        editingFileId ? undefined : handleExtractToFile
                      }
                      onExtractModulesToFiles={
                        editingFileId ? undefined : handleExtractModulesToFiles
                      }
                      onExplainWithAi={
                        editingFileId ? undefined : handleExplainWithAi
                      }
                      onImproveWithAi={
                        editingFileId ? undefined : handleImproveWithAi
                      }
                      onImproveParamsWithAi={
                        editingFileId ? undefined : handleImproveParamsWithAi
                      }
                      language={editorLanguage}
                      filePath={editorFilePath}
                      colorLineMap={colorLineMap}
                      hoveredParamLine={hoveredParamLine}
                    />
                  </div>
                </div>
              </CollapsiblePanelContent>
            </Panel>

            {!isProductMode && <PanelResizeHandle className="resize-handle" />}

            {/* 3D Preview Panel */}
            <Panel
              ref={previewPanelRef}
              order={2}
              minSize={4}
              collapsible={true}
              collapsedSize={4}
              onCollapse={() => setIsPreviewPanelCollapsed(true)}
              onExpand={() => setIsPreviewPanelCollapsed(false)}
            >
              <CollapsiblePanelContent
                isCollapsed={isPreviewPanelCollapsed}
                label="Preview"
                onExpand={() => previewPanelRef.current?.expand()}
                position="left"
              >
                <ViewerSection
                  scadCode={scadCode}
                  projectFiles={projectFiles}
                  onError={handleError}
                  onJumpToLine={handleJumpToLine}
                  onCollapse={() => previewPanelRef.current?.collapse()}
                  productMode={isProductMode}
                  cameraOffsetX={
                    isProductMode && parameters.length > 0 ? 168 : 0
                  }
                  parameters={parameters}
                  viewerState={viewerState}
                  onViewerStateChange={setViewerState}
                  onFitToBoundsReady={handleFitToBoundsReady}
                  onInsertCode={handleInsertCode}
                  onReplaceCode={handleReplaceCode}
                  onInteraction={handleInteraction}
                  forceRecompileKey={forceRecompileKey}
                  onCompiledColorsChange={setCompiledColors}
                   onPubSubPublish={handlePubSubPublish}
                   onSubscribeReady={handleSubscribeReady}
                   onCameraPathChange={setCameraPath}
                 />
              </CollapsiblePanelContent>
            </Panel>

            {!isProductMode && <PanelResizeHandle className="resize-handle" />}

            {/* Parameters Panel */}
            <Panel
              ref={parametersPanelRef}
              defaultSize={27}
              minSize={4}
              collapsible={true}
              collapsedSize={isProductMode ? 0 : 4}
              onCollapse={() => setIsParametersPanelCollapsed(true)}
              onExpand={() => setIsParametersPanelCollapsed(false)}
              order={3}
            >
              <CollapsiblePanelContent
                isCollapsed={isParametersPanelCollapsed}
                label="Parameters"
                onExpand={() => parametersPanelRef.current?.expand()}
                position="right"
                showStripe={!isProductMode}
              >
                <ParameterSection
                  parameters={parameters}
                  onParametersChange={handleParametersChange}
                  scadCode={scadCode}
                  projectFiles={projectFiles}
                  onFileAdd={handleFileAdd}
                  onCollapse={() => parametersPanelRef.current?.collapse()}
                  actionRef={parameterSectionActionRef}
                  codeSnapshots={codeSnapshots}
                  onParamHover={setHoveredParamName}
                  pubSubPublishRef={pubSubPublishRef}
                  pubSubSubscribeRef={pubSubSubscribeRef}
                />
              </CollapsiblePanelContent>
            </Panel>
          </PanelGroup>
        </div>

        {/* Files Panel - Bottom */}
        {!isProductMode && (
          <FileManager
            files={projectFiles}
            activeFileId={activeFileId}
            onFileSelect={handleFileSelect}
            onFileAdd={handleFileAdd}
            onFileDelete={handleFileDelete}
            onFileRename={handleFileRename}
            onInsertImport={handleInsertImport}
            onSplitSvg={handleSvgSplit}
            onCaptureMesh={handleCaptureMesh}
            projectTitle={title}
            missingImports={missingImports}
            isExpanded={filesExpanded}
            onExpandedChange={setFilesExpanded}
            isMainFileActive={editingFileId === null}
            onReturnToMain={handleReturnToMain}
            actionRef={fileManagerActionRef}
          />
        )}
      </div>
    </div>
  );

  return (
    <BlobProvider>
      <ColorProvider>
        <SceneSettingsProvider>
          {layout}

          {/* Product readiness score — reads SceneSettings context, syncs to parent state */}
          <ScadViewerProductScoreSync
            parameters={parameters}
            scadCode={scadCode}
            cameraPath={cameraPath}
            thumbnailUrl={thumbnailUrl}
            isPublic={isPublic}
            title={title}
            onScoreChange={handleScoreChange}
          />

          {/* Dialogs */}
          <SaveModelDialog
            open={showSaveDialog}
            onOpenChange={setShowSaveDialog}
            onSave={performSave}
            initialTitle={title}
            initialDescription={description}
            initialIsPublic={isPublic}
            initialDownloadPrice={downloadPrice}
            isNewModel={!currentModel}
            isSaving={isSaving}
          />

          <AlertDialog open={showNewConfirmDialog} onOpenChange={setShowNewConfirmDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Start a new model?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will clear the current code and parameters. Unsaved changes will be lost.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={confirmNewModel}>Clear & Start New</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {currentModel && profile?.username && (
            <ShareDialog
              open={showShareDialog}
              onOpenChange={setShowShareDialog}
              modelUrl={`/${profile.username}/${currentModel.slug}`}
              modelTitle={title}
              authorUsername={profile.username}
            />
          )}

          <VersionHistory
            open={showVersionHistory}
            onOpenChange={setShowVersionHistory}
            versions={versions}
            currentVersionId={currentModel?.current_version_id || null}
            onPreviewVersion={handlePreviewVersion}
            onRestoreVersion={handleRestoreVersion}
            isRestoring={restoreVersionMutation.isPending}
          />

          <ScreenshotDialog
            open={showScreenshotDialog}
            onOpenChange={setShowScreenshotDialog}
            onSetThumbnail={handleSetThumbnail}
            canSetThumbnail={!!currentModel}
          />

          <AiDialog
            open={showAiDialog}
            onOpenChange={(open) => {
              setShowAiDialog(open);
              if (!open) {
                setAiDialogInitialPrompt(undefined);
              }
            }}
            currentCode={scadCode}
            onCodeGenerated={handleAiCodeGenerated}
            initialMode={aiDialogMode}
            initialPrompt={aiDialogInitialPrompt}
            compileCode={async (code) => {
              // Create a temporary worker to test compilation
              return new Promise<{ success: boolean; error?: string }>(
                (resolve) => {
                  const testWorker = new Worker(
                    new URL('../worker/worker.ts', import.meta.url),
                    { type: 'module' },
                  );

                  const timeout = setTimeout(() => {
                    testWorker.terminate();
                    resolve({
                      success: false,
                      error: 'Compilation timeout after 30 seconds',
                    });
                  }, 30000);

                  testWorker.addEventListener('message', (event) => {
                    clearTimeout(timeout);
                    testWorker.terminate();

                    if (event.data.err) {
                      const error = event.data.err;
                      const errorMessage =
                        error instanceof OpenSCADError
                          ? error.message
                          : error?.message || 'Unknown compilation error';
                      resolve({ success: false, error: errorMessage });
                    } else if (event.data.data?.output) {
                      resolve({ success: true });
                    } else {
                      resolve({ success: false, error: 'No output generated' });
                    }
                  });

                  testWorker.addEventListener('error', (error) => {
                    clearTimeout(timeout);
                    testWorker.terminate();
                    resolve({ success: false, error: error.message });
                  });

                  testWorker.postMessage({
                    type: 'preview',
                    data: {
                      code,
                      params: [],
                      fileType: 'stl',
                    },
                  });
                },
              );
            }}
          />

          <AiExplainDialog
            open={showExplainDialog}
            onOpenChange={(open) => {
              setShowExplainDialog(open);
              if (!open) setExplainSelection(null);
            }}
            selectedCode={explainSelection?.selectedCode ?? ''}
            contextCode={explainSelection?.contextCode ?? ''}
          />

          <AiImproveDialog
            open={showImproveDialog}
            onOpenChange={(open) => {
              setShowImproveDialog(open);
              if (!open) setImproveSelection(null);
            }}
            selectedCode={improveSelection?.selectedCode ?? ''}
            contextCode={improveSelection?.contextCode ?? ''}
            onApply={handleApplyImprovedCode}
          />

          <AiImproveDialog
            open={showImproveParamsDialog}
            onOpenChange={(open) => {
              setShowImproveParamsDialog(open);
              if (!open) setImproveParamsCode(null);
            }}
            selectedCode={improveParamsCode ?? ''}
            contextCode={improveParamsCode ?? ''}
            title="Improve parameter config with AI"
            initialInstruction="Review and improve all parameter annotations: add meaningful ranges, descriptions, groups, visibility expressions, and markdown callouts where helpful."
            systemPromptOverride={buildImproveParamsSystemPrompt(
              improveParamsCode ?? '',
            )}
            onApply={handleApplyImprovedParams}
          />

          {/* Command Palette — inside SceneSettingsProvider so it can access SSAO/bloom/shadow context */}
          <ScadViewerCommandPaletteInner
            open={showCommandPalette}
            onOpenChange={setShowCommandPalette}
            editorRef={editorRef}
            onSave={handleSave}
            onSaveAs={handleSaveAs}
            onShare={
              currentModel && profile?.username ? handleShare : undefined
            }
            onScreenshot={handleScreenshot}
            onShowHistory={currentModel ? handleShowHistory : undefined}
            onAiAssist={handleAiAssist}
            onToggleVisibility={() => setIsPublic((p) => !p)}
            isPublic={isPublic}
            isOwner={true}
            hasModel={!!currentModel}
            viewerState={viewerState}
            onViewerStateChange={setViewerState}
            onFitToBounds={fitToBoundsCmd ?? undefined}
            parameters={parameters}
            onParametersChange={handleParametersChange}
            onCreateSnapshot={() =>
              parameterSectionActionRef.current?.openCreateSnapshotDialog()
            }
            onImportParameters={() =>
              parameterSectionActionRef.current?.triggerImportParameters()
            }
            scadCode={scadCode}
            onUploadFile={() => {
              setFilesExpanded(true);
              fileManagerActionRef.current?.triggerUpload();
            }}
            onCaptureMesh={() => {
              setFilesExpanded(true);
              fileManagerActionRef.current?.triggerCaptureMesh();
            }}
            onLayoutChange={handleLayoutChange}
            onProductMode={() => setIsProductMode(true)}
            onRecompile={() => setForceRecompileKey((k) => k + 1)}
            stlBlobRef={stlBlobRef}
            recents={recents}
            onRecordUsed={recordUsed}
            modelTitle={title}
            isDirty={isDirty}
            lastSavedAt={lastSavedAt}
            slashAiHandlers={{
              onAiCreate: handleAiCreate,
              onAiModify: handleAiModify,
              onAiExplain: handleAiExplainSlash,
              onAiImprove: handleAiImproveSlash,
              onAiFix: handleAiAssist,
            }}
          />
        </SceneSettingsProvider>
      </ColorProvider>
    </BlobProvider>
  );
}
